import { LOW_LIFT_THRESHOLD_PCT, NO_CHANGE_EPSILON_PCT } from "consts";

import { ContributionMarginYearBreakdown, GenerateSoilSightOutcomesResponse } from "models";

import { formatSignedPercent } from "pages/app/scenario-analysis/utils/comparisonFormatting";
import { bandAvg } from "pages/app/scenario-analysis/utils/minAvgMax";

/** Percentage change of `value` vs `baselineValue`, shared with CMComparisonTable. Null when the baseline is 0. */
export const calcUpliftPercent = (baselineValue: number, value: number): number | null =>
  baselineValue !== 0 ? ((value - baselineValue) / Math.abs(baselineValue)) * 100 : null;

/** Cumulative contribution margin over all projection years of one scenario outcome, on the api's avg band. */
export const sumContributionMargin = (years: ContributionMarginYearBreakdown[] | undefined): number =>
  (years ?? []).reduce((sum, year) => sum + bandAvg(year.contributionMargin?.total), 0);

export type LiftClassification = "negative" | "noChange" | "low" | "positive";

/** Shared lift-state rule: "low" drives the <5% variants, "negative"/"noChange" the flat ones. */
export const classifyLift = (upliftPct: number | null): LiftClassification => {
  if (upliftPct == null || Math.abs(upliftPct) <= NO_CHANGE_EPSILON_PCT) return "noChange";
  if (upliftPct < 0) return "negative";
  if (upliftPct < LOW_LIFT_THRESHOLD_PCT) return "low";
  return "positive";
};

/** Cumulative-CM uplift % of the change scenario vs the baseline scenario from one /run response. */
export const getScenarioUpliftPercent = (
  outcomes: GenerateSoilSightOutcomesResponse | null | undefined,
  baselineScenarioId: string | null | undefined,
  changeScenarioId: string | null | undefined
): number | null => {
  if (!outcomes || !baselineScenarioId || !changeScenarioId) return null;

  const baselineYears = outcomes.scenarios?.[baselineScenarioId]?.years;
  const changeYears = outcomes.scenarios?.[changeScenarioId]?.years;
  if (!baselineYears?.length || !changeYears?.length) return null;

  // Rounded first: the results page divides whole-euro cumulative totals, so unrounded sums here would drift a point apart.
  return calcUpliftPercent(Math.round(sumContributionMargin(baselineYears)), Math.round(sumContributionMargin(changeYears)));
};

/** Rounds and renders an uplift as one signed figure, e.g. "+13%". CMComparisonTable has its own spaced variant. */
export const formatUpliftPercent = (upliftPct: number): string => formatSignedPercent(Math.round(upliftPct));
